/**
 * SessionContext - WebSocket-based Analytics and Real-time Communication
 *
 * Manages WebSocket connection for real-time analytics, user session tracking,
 * and bidirectional communication between client and server.
 *
 * Key architectural decisions:
 * - Context for WebSocket: Single persistent connection shared across app
 *   prevents multiple WebSocket connections and ensures consistent state
 * - Analytics centralization: All analytics events flow through one channel
 *   simplifying tracking and reducing API calls
 * - Real-time capabilities: Enables live features like viewer counts,
 *   real-time notifications, and server-pushed updates
 *
 * Business rules:
 * - Session tracking: All user actions tracked for engagement analytics
 * - Username from JWT: User identity extracted from Cognito ID token
 * - Origin tracking: Multi-tenant support via subdomain identification
 * - Shared connection: Single WebSocket for entire app lifecycle
 *
 * Security considerations:
 * - Token-based auth: Username extracted from validated JWT
 * - Origin validation: Server validates subdomain for tenant isolation
 * - No sensitive data: Analytics events contain only non-PII metrics
 *
 * Performance optimizations:
 * - Connection sharing: `share: true` prevents duplicate connections
 * - Lazy connection: WebSocket only connects when username available
 * - Message batching: Server can batch analytics for efficiency
 *
 * @see src/utils/constants.js - WebSocket URL configuration
 * @see src/hooks/useToken.ts - JWT token management
 * @see .claude/api-integration-rules.md - WebSocket patterns
 */
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

import useWebSocket from 'react-use-websocket';
import { WEB_SOCKET_URL } from '../utils/constants';
import { useToken } from '../hooks/useToken';

/**
 * Analytics message payload interface
 *
 * WHY flexible type:
 * - Different analytics events have different shapes
 * - Server expects various event types (video_play, page_view, etc.)
 * - Record<string, unknown> allows any JSON-serializable data
 */
export interface AnalyticsMessage {
  /** Event-specific data */
  [key: string]: unknown;
  /** Origin URL (automatically added by sendMessage) */
  origin?: string;
  /** Username (automatically added by sendMessage) */
  username?: string | null;
}

/**
 * WebSocket message from server
 *
 * WHY minimal interface:
 * - Server controls message format
 * - Different message types have different structures
 * - Use discriminated union for type-safe message handling
 */
export interface ReceivedMessage {
  /** Message type or target action */
  target?: string;
  /** Message payload (varies by message type) */
  [key: string]: unknown;
}

/**
 * Session context value interface
 */
export interface SessionContextValue {
  /**
   * Send analytics message through WebSocket
   *
   * @param route - Server route/action for message handling
   * @param message - Analytics payload
   */
  sendMessage: (route: string, message: AnalyticsMessage) => void;

  /**
   * Last received message from server
   *
   * WHY typo preserved:
   * - Original code has "recievedMessage" (incorrect spelling)
   * - Preserving for backward compatibility with existing consumers
   * - TODO: Fix in major version update
   */
  recievedMessage: ReceivedMessage | null;
}

/**
 * Create context with undefined default
 *
 * WHY undefined instead of throwing:
 * - Allows context to exist before provider mounts
 * - Custom hook handles error boundary
 * - More flexible for testing
 */
const SessionContext = createContext<SessionContextValue | undefined>(undefined);

/**
 * SessionProvider props interface
 */
export interface SessionProviderProps {
  /** Child components needing session access */
  children: ReactNode;
}

/**
 * SessionProvider component that manages WebSocket connection and analytics.
 *
 * WHY WebSocket over REST for analytics:
 * - Real-time: Instant server notifications and live updates
 * - Efficiency: Single persistent connection vs multiple HTTP requests
 * - Bidirectional: Server can push updates to client
 * - Lower latency: No HTTP handshake overhead for each message
 *
 * @param props - Provider props
 */
export function SessionProvider({ children }: SessionProviderProps): JSX.Element {
  // WHY: Extract username from JWT for user identification
  // Used for personalized analytics and multi-user session tracking
  const [username, setUsername] = useState<string | null>(null);
  const { idToken, decodeIdToken } = useToken();

  // WHY: Store received messages for real-time features
  // Enables server-pushed notifications, live viewer counts, alerts
  // Note: Typo in variable name preserved for backward compatibility
  const [recievedMessage, setRecievedMessage] = useState<ReceivedMessage | null>(null);

  // WHY: Update username when token changes (login/logout)
  // Ensures analytics always tied to correct user
  // WHY username field: Cognito ID tokens contain 'username' claim for user identification
  useEffect(() => {
    setUsername(() => decodeIdToken()?.username ?? null);
  }, [idToken]);

  // WHY: WebSocket connection with query params for server routing
  // username: User identification for personalized features
  // origin: Subdomain for multi-tenant channel isolation
  // share: true prevents duplicate connections across components
  const { sendJsonMessage, lastMessage } = useWebSocket(WEB_SOCKET_URL, {
    queryParams: { username: username ?? undefined, origin: window.location.hostname },
    share: true, // CRITICAL: Prevents multiple WebSocket connections
  });

  // WHY: Process incoming WebSocket messages
  // "clear" target resets state for new session or logout
  // Other messages parsed and stored for component consumption
  useEffect(() => {
    if (lastMessage?.data) {
      try {
        // Parse message data (may be string or object)
        const messageData =
          typeof lastMessage.data === 'string'
            ? (JSON.parse(lastMessage.data) as ReceivedMessage)
            : (lastMessage.data as ReceivedMessage);

        // WHY: Special "clear" command for state reset
        // Used during logout or session expiry
        if (messageData.target === 'clear') {
          setRecievedMessage(null);
          return;
        }

        // WHY: Store non-clear messages
        // Server sends JSON that components can react to
        setRecievedMessage(messageData);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    }
  }, [lastMessage]);

  /**
   * Sends analytics message through WebSocket.
   *
   * WHY include metadata with every message:
   * - route: Server-side routing for different analytics types
   * - origin: Multi-tenant identification
   * - username: User attribution for analytics
   *
   * @param route - Server route/action for message handling
   * @param message - Analytics payload
   */
  function sendMessage(route: string, message: AnalyticsMessage): void {
    sendJsonMessage({
      action: route,
      message: { ...message, origin: window.location.origin, username },
    });
  }

  const contextValue: SessionContextValue = {
    // WHY: Minimal API surface for clear usage patterns
    // sendMessage: For outbound analytics
    // recievedMessage: For inbound server updates
    sendMessage,
    recievedMessage,
  };

  return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>;
}

/**
 * Custom hook for WebSocket analytics and real-time features.
 *
 * WHY named useSessionAnalytics instead of useSession:
 * - Clear purpose: Specifically for analytics and real-time features
 * - Avoid confusion: Not for auth session (that's AuthContext)
 * - Future-proof: Can add more session features without breaking changes
 *
 * WHY custom hook pattern:
 * - Error boundaries: Clear error when used outside provider
 * - Abstraction: Hides WebSocket complexity from consumers
 * - Type safety: Single place for TypeScript types
 *
 * @returns Analytics methods and received messages
 * @throws {Error} If used outside SessionProvider
 */
export function useSessionAnalytics(): SessionContextValue {
  const context = useContext(SessionContext);

  // WHY: Developer experience - clear error for common mistake
  if (context === undefined) {
    throw new Error('useSessionAnalytics must be used within SessionProvider');
  }

  return context;
}
